import { useCallback, useEffect, useState } from "react";

import { formatDate } from "./Records";
import { admin, AdminError, type AutocheckRun, type Finding } from "./api";

const SEVERITY_LABEL: Record<Finding["severity"], string> = {
  high: "pilne",
  medium: "do przejrzenia",
  low: "szum",
};

const STATUS_LABEL: Record<AutocheckRun["status"], string> = {
  never: "nigdy nie uruchamiany",
  running: "trwa",
  ok: "bez zastrzeżeń",
  findings: "są znaleziska",
  error: "audyt się wywalił",
};

/* Datę składa wspólny formatDate — jeden szablon w całym panelu. */

/**
 * Audyt systemu: co jest zepsute i co z tym zrobić.
 *
 * Kolejność znalezisk idzie po skali, nie po samym istnieniu — trzy zablokowane
 * procesy to szum, trzydzieści to awaria. Panel, który świeci na czerwono
 * zawsze, po tygodniu przestaje być czytany.
 */
export function Autocheck() {
  const [runs, setRuns] = useState<AutocheckRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    admin
      .autocheck()
      .then(setRuns)
      .catch((caught) => setError((caught as AdminError).message));
  }, []);

  useEffect(load, [load]);

  const start = async (checkId: string) => {
    setBusy(checkId);
    setError(null);
    try {
      const fresh = await admin.runAutocheck(checkId);
      setRuns((current) =>
        (current ?? []).map((run) => (run.check_id === checkId ? fresh : run)),
      );
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(null);
    }
  };

  if (error && !runs) return <p className="error">{error}</p>;
  if (!runs) return <div className="spinner" />;

  return (
    <div className="adm__checks">
      {error && <p className="error">{error}</p>}

      {runs.map((run) => (
        <article key={run.check_id} className="adm__check">
          <header className="adm__checkhead">
            <div>
              <h3 className="adm__subtitle">{run.title}</h3>
              <p className="adm__muted">
                {STATUS_LABEL[run.status]}
                {run.started_at && ` · ${formatDate(run.started_at)}`}
              </p>
            </div>
            <span className="adm__spacer" />
            <button
              className="btn btn--ghost"
              onClick={() => start(run.check_id)}
              disabled={busy === run.check_id}
            >
              {busy === run.check_id ? "Sprawdzam…" : "Uruchom teraz"}
            </button>
          </header>

          {run.status === "error" && <p className="error">{run.error}</p>}
          {run.status === "ok" && <p className="adm__muted">Nic do zgłoszenia.</p>}

          {run.findings.map((finding) => (
            <div key={finding.code} className={`adm__finding adm__finding--${finding.severity}`}>
              <p className="adm__findingtitle">
                {finding.title}
                <span className="adm__tag">{SEVERITY_LABEL[finding.severity]}</span>
              </p>
              <p className="adm__muted">{finding.cause}</p>
              <p className="adm__action">→ {finding.action}</p>
              {finding.sample_ids.length > 0 && (
                <p className="adm__mono adm__samples">{finding.sample_ids.join(", ")}</p>
              )}
            </div>
          ))}
        </article>
      ))}
    </div>
  );
}
